import React, { useState } from 'react'
import LectureAttendanceModal from './components/LectureAttendanceModal'
import { useNavigate } from 'react-router-dom'
import SharedFilters from '../../../components/SharedFilters'
import AppActionMenu from '../../../components/AppActionMenu'
import AppPagination from '../../../components/AppPagination'
import PredefinedReportsDrawer from '../../../components/PredefinedReportsDrawer'
import useLectureListScreen, { STATUS_TABS } from './hooks/useLectureListScreen'

export default function LectureListScreen() {
  const navigate = useNavigate()
  const { state, actions } = useLectureListScreen()
  const [attendanceModalLectureId, setAttendanceModalLectureId] = useState(null)

  const toolsConfig = [
    { key: 'reports-drawer', label: 'Predefined Reports', icon: 'description' },
    { key: 'export_excel', label: 'Export Excel', icon: 'table_view' },
    { key: 'export_pdf', label: 'Export PDF', icon: 'picture_as_pdf' },
    { key: 'combined-sms', label: 'Send Combined SMS', icon: 'sms' },
    { key: 'combined-whatsapp', label: 'Send Combined WhatsApp', icon: 'chat' },
    { key: 'create-entries', label: 'Create Entries (Timetable)', icon: 'schedule' },
    { key: 'upload', label: 'Upload Schedule', icon: 'cloud_upload' },
  ]

  // Status badge styling helper matching legacy color scheme
  const renderStatusBadge = (status) => {
    switch (status) {
      case 'Cancelled':
        return (
          <span className="inline-flex items-center gap-1 rounded-full bg-rose-50 px-2.5 py-0.5 text-[11px] font-bold text-rose-700 ring-1 ring-rose-200">
            <span className="material-symbols-outlined text-[13px]">cancel</span>
            Cancelled
          </span>
        )
      case 'Late':
        return (
          <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-0.5 text-[11px] font-bold text-amber-700 ring-1 ring-amber-200">
            <span className="material-symbols-outlined text-[13px]">schedule</span>
            Late
          </span>
        )
      case 'Completed':
        return (
          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-0.5 text-[11px] font-bold text-emerald-700 ring-1 ring-emerald-200">
            <span className="material-symbols-outlined text-[13px]">check_circle</span>
            Completed
          </span>
        )
      case 'Scheduled':
      default:
        return (
          <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2.5 py-0.5 text-[11px] font-bold text-blue-700 ring-1 ring-blue-200">
            <span className="material-symbols-outlined text-[13px]">mic</span>
            Scheduled
          </span>
        )
    }
  }

  return (
    <div className="mx-auto flex w-full max-w-[1460px] flex-col gap-6 pb-16">
      {/* ── Header Title & Segmented Status Tabs (Rule 87: No Local Add CTA) ── */}
      <section className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-display text-4xl font-extrabold leading-[0.98] tracking-[-0.02em] text-on-surface">
            List of Lectures
          </h1>
          <p className="mt-1 text-xs font-medium text-on-surface-variant">
            View, track, and manage academic lecture schedules, attendance records, and cancellations.
          </p>
        </div>

        {/* Actions Cluster: Segmented Status Tabs */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex w-fit items-center gap-1.5 rounded-2xl bg-surface-container-high p-1 shadow-xs">
            {STATUS_TABS.map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => actions.handleStatusTabChange(tab.key)}
                className={`cursor-pointer select-none rounded-xl px-4 py-2 text-xs font-bold transition-all ${
                  state.statusFilter === tab.key
                    ? 'bg-primary text-on-primary shadow-xs'
                    : 'text-on-surface-variant hover:bg-surface-container-highest/60 hover:text-on-surface'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ── Filters Bar (SharedFilters with 100% legacy parity) ── */}
      <SharedFilters
        searchField={state.searchField}
        searchText={state.searchText}
        setSearchField={actions.setSearchField}
        setSearchText={actions.setSearchText}
        searchFieldOptions={state.options.searchField}
        onSearch={() => actions.loadData()}
        onReset={actions.handleReset}
        showStatus={false}
        companyFilter={state.companyFilter}
        setCompanyFilter={actions.setCompanyFilter}
        companyOptions={state.options.company}
        branchFilter={state.branchFilter}
        setBranchFilter={actions.setBranchFilter}
        branchOptions={state.options.branch}
        courseFilter={state.courseFilter}
        setCourseFilter={actions.setCourseFilter}
        courseOptions={state.options.course}
        batchFilter={state.batchFilter}
        setBatchFilter={actions.setBatchFilter}
        batchOptions={state.options.batch}
        subjectFilter={state.subjectFilter}
        setSubjectFilter={actions.setSubjectFilter}
        subjectOptions={state.options.subject}
        facultyFilter={state.facultyFilter}
        setFacultyFilter={actions.setFacultyFilter}
        facultyOptions={state.options.faculty}
        categoryFilter={state.categoryFilter}
        setCategoryFilter={actions.setCategoryFilter}
        categoryOptions={state.options.category}
        academicYearFilter={state.academicYearFilter}
        setAcademicYearFilter={actions.setAcademicYearFilter}
        academicYearOptions={state.options.academicYear}
        periodFilter={state.periodFilter}
        setPeriodFilter={actions.setPeriodFilter}
        periodOptions={state.options.period}
        fromDate={state.fromDate}
        setFromDate={actions.setFromDate}
        toDate={state.toDate}
        setToDate={actions.setToDate}
        visibleAccordionFilters={[
          'company',
          'branch',
          'course',
          'batch',
          'subject',
          'faculty',
          'category',
          'academicYear',
          'period',
          'fromDate',
          'toDate',
        ]}
        tools={toolsConfig}
        onToolSelect={actions.handleToolSelect}
      />

      {/* ── Lectures Table ── */}
      <section className="overflow-hidden rounded-2xl border border-outline-variant/25 bg-surface-container-lowest shadow-xs">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[840px] text-left text-xs text-on-surface">
            <thead className="border-b border-outline-variant/25 bg-surface-container-low/70 text-[11px] font-black uppercase tracking-[0.1em] text-on-surface-variant">
              <tr>
                <th className="px-5 py-3.5">Course / Faculty</th>
                <th className="px-5 py-3.5">Date &amp; Time</th>
                <th className="px-5 py-3.5">Subject / Topic</th>
                <th className="px-5 py-3.5">Branch / Batch</th>
                <th className="px-5 py-3.5">Status</th>
                <th className="px-5 py-3.5">Attendance</th>
                <th className="w-16 px-4 py-3.5 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-outline-variant/15">
              {state.loading ? (
                <tr>
                  <td colSpan={7} className="py-14 text-center text-on-surface-variant">
                    <div className="flex flex-col items-center justify-center gap-2">
                      <span className="material-symbols-outlined animate-spin text-2xl text-primary">progress_activity</span>
                      <span className="text-xs font-semibold">Loading lectures...</span>
                    </div>
                  </td>
                </tr>
              ) : state.items.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-14 text-center text-on-surface-variant">
                    <div className="flex flex-col items-center justify-center gap-2">
                      <span className="material-symbols-outlined text-3xl text-outline">event_busy</span>
                      <span className="font-bold text-on-surface">No lectures found</span>
                      <span className="text-xs">Try adjusting your filter criteria or status filter.</span>
                    </div>
                  </td>
                </tr>
              ) : (
                state.items.map((item) => {
                  const isCancelled = item.is_cancelled || item.status === 'Cancelled'
                  const facultyInitials = (item.faculty_name || 'FC')
                    .split(' ')
                    .filter(Boolean)
                    .slice(0, 2)
                    .map((w) => w[0])
                    .join('')
                    .toUpperCase()

                  const actionItems = [
                    {
                      key: 'edit',
                      label: 'Edit',
                      icon: 'edit',
                    },
                    {
                      key: 'attendance',
                      label: 'Attendance',
                      icon: 'how_to_reg',
                    },
                    {
                      key: 'sms',
                      label: 'Send SMS',
                      icon: 'sms',
                    },
                    {
                      key: 'whatsapp',
                      label: 'Send WhatsApp',
                      icon: 'chat',
                    },
                    {
                      key: 'toggle-cancel',
                      label: isCancelled ? 'Restore Lecture' : 'Cancel Lecture',
                      icon: isCancelled ? 'restore' : 'cancel',
                      danger: !isCancelled,
                    },
                    {
                      key: 'delete',
                      label: 'Delete',
                      icon: 'delete',
                      danger: true,
                    },
                  ]

                  return (
                    <tr
                      key={item.id}
                      onClick={() => navigate(`/lectures/entry?id=${item.id}`)}
                      className={`cursor-pointer transition hover:bg-surface-container-low/40 ${
                        isCancelled ? 'bg-rose-50/20 opacity-75' : ''
                      }`}
                    >
                      {/* Course / Faculty */}
                      <td className="px-5 py-3.5">
                        <div className="flex flex-col gap-1.5">
                          <span className="inline-flex self-start rounded-xl bg-primary/10 px-2 py-0.5 text-[11px] font-extrabold text-primary">
                            {item.course_name || 'Course'}
                          </span>
                          <div className="flex items-center gap-2">
                            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-surface-container-high text-[10px] font-black text-on-surface">
                              {facultyInitials}
                            </span>
                            <span className="font-bold text-on-surface">{item.faculty_name || 'Unassigned'}</span>
                          </div>
                        </div>
                      </td>

                      {/* Date & Time */}
                      <td className="px-5 py-3.5">
                        <div className="flex flex-col gap-0.5">
                          <span className="font-semibold text-on-surface">{item.p_date || '-'}</span>
                          <span className="text-[11px] text-on-surface-variant">
                            {item.p_from || '--'} - {item.p_till || '--'}
                          </span>
                        </div>
                      </td>

                      {/* Subject / Topic */}
                      <td className="px-5 py-3.5">
                        <div className="flex flex-col gap-1">
                          <span className="font-bold text-on-surface">{item.subject_name || '-'}</span>
                          <span className="line-clamp-1 text-[11px] text-on-surface-variant">
                            {item.chapter_name ? `${item.chapter_no ? item.chapter_no + ' - ' : ''}${item.chapter_name}` : item.topic || '-'}
                          </span>
                          {item.classroom_name && (
                            <div className="flex items-center gap-1 text-[10px] text-on-surface-variant">
                              <span className="material-symbols-outlined text-[13px]">meeting_room</span>
                              <span>{item.classroom_name}</span>
                            </div>
                          )}
                        </div>
                      </td>

                      {/* Branch / Batch */}
                      <td className="px-5 py-3.5">
                        <div className="flex flex-col gap-1">
                          <span className="font-semibold text-on-surface">{item.branch_names || 'All Branches'}</span>
                          <span className="inline-flex self-start rounded-xl bg-surface-container-high px-2 py-0.5 text-[10px] font-bold text-on-surface-variant">
                            {item.batch_names || 'All Batches'}
                          </span>
                        </div>
                      </td>

                      {/* Status */}
                      <td className="px-5 py-3.5">
                        {renderStatusBadge(item.status)}
                      </td>

                      {/* Attendance */}
                      <td className="px-5 py-3.5">
                        {item.total_count > 0 ? (
                          <div className="flex flex-col gap-0.5">
                            <span className="font-bold text-on-surface">
                              {item.present_count} / {item.total_count}
                            </span>
                            <span className="text-[10px] text-on-surface-variant">
                              ({Math.round((item.present_count / item.total_count) * 100)}% Present)
                            </span>
                          </div>
                        ) : (
                          <span className="text-on-surface-variant/60">-</span>
                        )}
                      </td>

                      {/* Action Menu (Three-Dot AppActionMenu with stopPropagation) */}
                      <td className="px-4 py-3.5 text-right" onClick={(e) => e.stopPropagation()}>
                        <AppActionMenu
                          triggerLabel=""
                          triggerIcon="more_vert"
                          showChevron={false}
                          triggerClassName="rounded-lg p-1.5 text-outline-variant transition-colors hover:bg-surface-container-low hover:text-primary bg-transparent cursor-pointer"
                          items={actionItems}
                          onSelect={(key) => {
                            if (key === 'edit') {
                              navigate(`/lectures/entry?id=${item.id}`)
                            } else if (key === 'attendance') {
                              setAttendanceModalLectureId(item.id)
                            } else if (key === 'sms') {
                              actions.handleSendSMS(item)
                            } else if (key === 'whatsapp') {
                              actions.handleSendWhatsApp(item)
                            } else if (key === 'toggle-cancel') {
                              actions.handleToggleCancel(item)
                            } else if (key === 'delete') {
                              actions.handleDelete(item)
                            }
                          }}
                        />
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>

        {/* ── Table Footer & Standard AppPagination ── */}
        <div className="flex flex-col items-center justify-between gap-4 border-t border-outline-variant/15 px-6 py-4 sm:flex-row bg-surface-container-lowest rounded-2xl shadow-xs">
          <p className="text-xs font-medium text-on-surface-variant">
            Showing {state.totalCount === 0 ? 0 : (state.page - 1) * state.pageSize + 1} to{' '}
            {Math.min(state.page * state.pageSize, state.totalCount)} of {state.totalCount} entries
          </p>

          <AppPagination
            page={state.page}
            totalPages={state.totalPages}
            onPageChange={actions.setPage}
            onPrevious={() => actions.setPage((p) => Math.max(1, p - 1))}
            onNext={() => actions.setPage((p) => Math.min(state.totalPages, p + 1))}
            canPrevious={state.page > 1}
            canNext={state.page < state.totalPages}
          />
        </div>
      </section>

      {/* ── Lecture Attendance Modal ── */}
      <LectureAttendanceModal
        isOpen={Boolean(attendanceModalLectureId)}
        lectureId={attendanceModalLectureId}
        onClose={() => setAttendanceModalLectureId(null)}
        onSuccess={actions.refresh}
      />

      {/* ── Predefined Reports Drawer ── */}
      <PredefinedReportsDrawer
        isOpen={state.isReportsOpen}
        onClose={() => actions.setIsReportsOpen(false)}
        title="Lecture Reports"
        module="education"
        submodule="lectures"
        filters={{
          academicYear: state.academicYearFilter,
          companyId: state.companyFilter,
          branchId: state.branchFilter,
          courseId: state.courseFilter,
          batchId: state.batchFilter,
          subjectId: state.subjectFilter,
          facultyId: state.facultyFilter,
          fromDate: state.fromDate,
          toDate: state.toDate,
          status: state.statusFilter,
          category: state.categoryFilter,
          searchField: state.searchField,
          searchText: state.searchText,
        }}
      />
    </div>
  )
}
